<template>
    <div class="dashboard-page flex flex-col flex-grow justify-center">
        <Toast position="top-center" />
        <div class="page-content" role="main">
            <CandidateProfileCard :showHeader="true"></CandidateProfileCard>
            <section
                v-if="applications && applications.length && !loading"
                class="aplications-section mb-3"
                :aria-label="$t('lib_applications')"
                aria-busy="false"
            >
                <h2 class="section-title fw-bold py-3">
                    {{ $t('lib_applications') }}
                </h2>
                <div
                    v-for="(job, index) in applications"
                    :key="job.applicationId"
                    class="job card"
                    :class="'job-' + index"
                    :aria-label="job?.name"
                >
                    <div class="card-header d-flex align-items-center bg-white">
                        <p class="job-title d-flex align-items-center">
                            <span>
                                {{
                                   job?.name
                                }}
                                -
                                {{
                                    isOfficialType(job) || isCataType(job)
                                        ? job?.jobTitle
                                        : $t(`${job.applicationType.resourceKey}`)
                                }}  
                            </span>
                            
                            <span
                                class="job-status py-1 fw-bold d-flex align-items-center"
                                :class="job.status"
                                v-tooltip.left.hover.focus="
                                    $t(`status_${job.status.toLowerCase()}_info`)
                                "
                            >
                                <span class="job-status-text">
                                    &nbsp;({{ $t(`st_${job.status.toLowerCase()}`) }})
                                </span>
                            </span>
                            
                            <span
                                v-if="job.hasPendingActions"
                                class="text-warning h3 ms-2 mb-0"
                                v-tooltip.top.hover.focus="
                                    $t(`lib_actions_warning`)
                                "
                            >
                                <font-awesome-icon icon="fas fa-exclamation-triangle"/>
                            </span>

                        </p>
                        
                        <div
                            class="job-actions d-flex align-items-center justify-content-end mt-auto"
                        >
                            <!-- based on IsActive we should handle the label and action for view-btn -->
                            <a
                                v-if="job.hasActions"
                                type="button"
                                :href="`/${selectedLanguage}/todo?applicationId=${job.applicationId}`"
                                class="action-btn px-6 py-1 me-4 text-primary"
                                role="button"
                                :aria-label="$t('btn_actions')"
                            >
                                <font-awesome-icon icon="fas fa-exclamation-triangle"/>
                                <span>{{ $t('btn_actions') }}</span>
                            </a>
                            <a
                                v-if="job.isActive && job?.status !=='PRESELECTED' && job?.status !== 'SELECTED'"
                                type="button"
                                :href="`/${selectedLanguage}/wizard?id=${job.applicationId}&type=${job.applicationType.code}`"
                                class="edit-btn px-6 py-1 me-4 text-primary"
                                role="button"
                                :aria-label="$t('btn_edit')"
                            >
                                <font-awesome-icon icon="fas fa-pencil"/>
                                <span>{{ $t('btn_edit') }}</span>
                            </a>
                            <a
                                v-if="
                                    !job.isActive || job?.status !== 'DRAFT'
                                "
                                type="button"
                                :href="`/${selectedLanguage}/Application?id=${job.applicationId}`"
                                class="view-btn px-6 py-1 me-4 text-primary"  
                                role="button"
                                :aria-label="$t('btn_view')"
                            >
                                <font-awesome-icon icon="fas fa-eye"/>
                                <span>{{ $t('btn_view') }}</span>
                            </a>
                            <WithdrawApplicationModal v-if="
                                    job?.status !== 'WITHDRAWN'"
                                :application-id="job.applicationId"
                                @withdraw-complete="onWithdrawComplete"
                            />
                        </div>
                    </div>
                </div>
            </section>
            <div v-if="loading" class="card aplications-skeleton mb-5" aria-busy="true">
                <div class="card-header d-flex justify-content-between py-3">
                    <Skeleton width="12rem"></Skeleton>
                    <Skeleton width="8rem"></Skeleton>
                </div>
                <div class="card-body d-flex justify-content-between flex-wrap">
                    <div>
                        <Skeleton width="10rem" class="mb-2"></Skeleton>
                        <Skeleton width="5rem" class="mb-2"></Skeleton>
                        <Skeleton width="7rem" class="mb-2"></Skeleton>
                        <Skeleton width="8rem" class="mb-2"></Skeleton>
                    </div>
                    <div>
                        <Skeleton
                            width="14rem"
                            class="ms-md-auto mb-2"
                        ></Skeleton>
                        <Skeleton
                            width="12rem"
                            class="ms-md-auto mb-2"
                        ></Skeleton>
                        <div class="d-flex justify-content-between flex-wrap">
                            <Skeleton
                                width="6rem"
                                height="2rem"
                                class="ms-md-auto mb-2"
                            ></Skeleton>
                            <Skeleton
                                width="6rem"
                                height="2rem"
                                class="ms-md-auto mb-2"
                            ></Skeleton>
                        </div>
                    </div>
                </div>
            </div>
            <section
                v-if="openings && openings.length && !loading"
                class="openings-section mb-5"
                :aria-label="$t('lib_work')"
                aria-busy="false"
            >
                <h2 class="section-title fw-bold py-3">
                    {{ $t('lib_work') }}
                </h2>
                <div class="card">
                    <div class="card-body p-0">
                        <TabView :activeIndex="0">
                            <TabPanel
                                v-for="(data, i) in openingTypes"
                                :key="i"
                            >
                                <template #header>
                                    <font-awesome-icon :icon="data.icon"/>
                                    <span
                                        v-tooltip.top.hover.focus="
                                            data.description
                                        "
                                        :aria-label="data.name + '-' + data.openings.length "
                                    >
                                        {{ data.name }} ({{ data.openings.length }})
                                    </span>
                                </template>
                                <template v-for="(job, index) in data.openings">
                                    <OpeningCard
                                        v-if="data?.openings"
                                        :key="index"
                                        :job="job"
                                        @open-import-dialog="
                                            openImportDialog(
                                                job.applicationType.code
                                            )
                                        "
                                        :positionData="data"
                                    ></OpeningCard>
                                </template>

                                <div
                                    v-if="!data?.openings?.length"
                                    class="no-openings-found"
                                >
                                    <h3 class="no-record-found mt-5" :aria-label="$t('lib_no_cycle')">
                                        {{ $t('lib_no_cycle') }}
                                    </h3>
                                </div>
                            </TabPanel>
                        </TabView>
                    </div>
                </div>
            </section>
            <div v-if="loading" class="card openings-skeleton mb-5" aria-busy="true">
                <div class="card-header d-flex justify-content-between py-3">
                    <Skeleton width="12rem"></Skeleton>
                    <Skeleton width="8rem"></Skeleton>
                </div>
                <div class="card-body d-flex justify-content-between flex-wrap">
                    <div>
                        <Skeleton width="10rem" class="mb-2"></Skeleton>
                        <Skeleton width="7rem" class="mb-2"></Skeleton>
                        <Skeleton width="4rem" class="mb-2"></Skeleton>
                        <Skeleton width="7rem" class="mb-2"></Skeleton>
                    </div>
                    <div>
                        <Skeleton
                            width="14rem"
                            class="ms-md-auto mb-2"
                        ></Skeleton>
                        <Skeleton
                            width="12rem"
                            class="ms-md-auto mb-2"
                        ></Skeleton>
                        <Skeleton
                            width="6rem"
                            height="2rem"
                            class="ms-md-auto mb-2"
                        ></Skeleton>
                    </div>
                </div>
            </div>
        </div>
        <Dialog
            :modal="true"
            :dismissableMask="true"
            v-model:visible="importDialogIsOpen"
            :showHeader="false"
            class="dialog-rounded-content"
            id="import-application-modal"
        >
            <h2
                class="dialog-title mb-3 text-center"
                :class="isCORSite ? 'text-cor-green' : 'text-eesc-blue-400'"
            >
                <font-awesome-icon icon="far fa-file-import"/>
                {{ $t('btn_import') }}
            </h2>
            <form
                method="post"
                class="import-form"
                @submit.prevent="importApplicationData"
            >
                <div class="my-5">
                    <p class="mb-4">
                        {{ $t('lib_import_info') }}
                    </p>
                    <div class="form-group mb-3">
                        <input
                            type="text"
                            :placeholder="$t('lib_account_id')"
                            v-model="id"
                            class="form-control"
                            name="id"
                        />
                    </div>
                    <div class="form-group mb-3">
                        <input
                            type="email"
                            :placeholder="$t('lib_email')"
                            v-model="email"
                            class="form-control"
                            name="email"
                        />
                    </div>
                </div>
                <div class="mt-4 d-flex justify-content-between">
                    <button
                        type="submit"
                        class="confirm-import-btn btn btn-lg btn-success px-3 py-1"
                        :disabled="loading"
                    >
                        {{ $t('btn_import') }}
                    </button>
                    <button
                        type="button"
                        @click="closeImportDialog"
                        class="cancel-import-btn btn btn-lg btn-outline-secondary px-3 py-1"
                    >
                        {{ $t('btn_cancel') }}
                    </button>
                </div>
            </form>
        </Dialog>
    </div>
    <AccountWarningDialog />
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import CandidateProfileCard from '@/components/CandidateProfileCard.vue';
import Skeleton from 'primevue/skeleton';
import Toast from 'primevue/toast';
import WithdrawApplicationModal from '@/components/WithdrawApplicationModal.vue';
import AccountWarningDialog from '../components/AccountWarningDialog.vue';
import Dialog from 'primevue/dialog';
// import SelectButton from 'primevue/selectbutton';
// import Chip from 'primevue/chip';
import OpeningCard from '../components/OpeningCard.vue';
// import Accordion from 'primevue/accordion';
// import AccordionTab from 'primevue/accordiontab';

import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';
import CommonMixins from '../mixins';

export default {
    name: 'DashboardPage',
    components: {
        CandidateProfileCard,
        Skeleton,
        Toast,
        WithdrawApplicationModal,
        Dialog,
        // SelectButton,
        // Chip,
        OpeningCard,
        // Accordion,
        // AccordionTab,
        TabView,
        TabPanel,
        AccountWarningDialog
    },
    props: {
        appData: {
            type: String,
        },
    },
    data() {
        return {
            loading: false,
            id: '',
            email: '',
            importDialogIsOpen: false,
            selectedApplicationId: null,
            selectedApplicationType: null,
            selectedOpeningsInterests: null,
        };
    },
    computed: {
        ...mapGetters([
            'isCORSite',
            'currentSite',
            'selectedLanguage',
            'applicationStatusIcon',
            'applications',
            'openings',
            'unitDisplayName',
            'functions',
            'functionLabel',
            'applicantCategories',
            'applicantCategoryLabel',
        ]),
        applicationsWithPendingActions() {
            return this.applications.filter(
                (application) => application.hasPendingActions
            );
        },
        cycleTypesBasedOnOpeningsInterests() {
            // gather a list of selected cycleTypes so we can filter openings based on them;
            return this.selectedOpeningsInterests?.flatMap(
                (val) => val?.cycleTypes
            );
        },
        openingTypes() {
            return [
                {
                    name: this.$t('lib_officials_tab'),
                    cardName: this.$t('lib_permanent'),
                    description: this.$t('lib_permanent_info'),
                    code: 'permanent',
                    openings: this.filteredOpenings(['OFFICIAL']),
                    icon: 'fas fa-id-badge'
                },
                {
                    name: this.$t('lib_agents_tab'),
                    cardName: '',
                    description: this.$t('lib_temporary_info'),
                    code: 'temporary',
                    openings: this.filteredOpenings(['CA', 'TA']),
                    icon: 'fas fa-user-clock'
                },
                {
                    name: this.$t('lib_traineeships'),
                    cardName: this.$t('lib_traineeships'),
                    description: this.$t('lib_traineeships_info'),
                    code: 'traineeships',
                    openings: this.filteredOpenings([
                        'SHORT',
                        'TRAINEE',
                        'TRAINGOV',
                    ]),
                    icon: 'fas fa-user-graduate'
                }
            ];
        },
    },
    methods: {
        ...mapActions([
            'loadAppData',
            'loadApplications',
            'loadOpenings',
            'importApplication',
            'createApplication',
        ]),
        getData() {
            this.loading = true;
            this.loadApplications().then(() => {
                this.loading = false;
                this.showPendingActionsAlert();
            });
            this.loadOpenings().then(() => {
                this.loading = false;
            });
        },
        importApplicationData() {
            if (this.id && this.email && this.selectedApplicationType) {
                this.loading = true;
                this.importApplication({
                    user: {
                        id: this.id,
                        email: this.email,
                    },
                    applicationType: this.selectedApplicationType,
                }).then(
                    () => {
                        this.getData();
                        this.closeImportDialog();
                        this.loading = false;
                    },
                    () => {
                        this.$toast.add({
                            severity: 'error',
                            detail: this.$t('mess_notauthenticated'),
                            life: 3000,
                        });
                        this.loading = false;
                    }
                );
            } else {
                this.$toast.add({
                    severity: 'error',
                    detail: this.$t('cand_validationmessage'),
                    life: 3000,
                });
                this.loading = false;
            }
        },
        closeImportDialog() {
            this.importDialogIsOpen = false;
        },
        openImportDialog(applicationType) {
            this.importDialogIsOpen = true;
            this.selectedApplicationType = applicationType;
        },
        applyToJob(applicationCycleId, applicationType) {
            this.loading = true;
            this.createApplication({
                applicationCycleId: applicationCycleId,
            }).then(
                (applicationId) => {
                    location.href = `${location.origin}/${this.selectedLanguage}/wizard?id=${applicationId}&type=${applicationType}`;
                },
                (error) => {
                    this.loading = false;
                    this.$toast.add({
                        severity: 'error',
                        detail: error.title,
                        life: 3000,
                    });
                }
            );
        },
        onWithdrawComplete() {
            this.getData();
        },
        showPendingActionsAlert() {
            if (this.applicationsWithPendingActions.length) {
                this.applicationsWithPendingActions.map((application) => {
                    console.log('application', application);
                    this.$toast.add({
                        severity: 'warn',
                        detail: this.$t('lib_actions_warning', [
                            this.$t(application.applicationType.resourceKey),
                        ]),
                        // detail: `You have pending actions for "${ this.$t(application.applicationType.resourceKey) }" application!`,
                    });
                });
            }
        },
        isOfficialType(job) {     
            return job?.applicationType?.code?.toLowerCase() === 'official';
        },
        isCataType(job) {
            return job?.applicationType?.code?.toLowerCase() === 'ca' || job?.applicationType?.code?.toLowerCase() === 'ta';
        },
        clearSelectedOpeningsInterests() {
            this.selectedOpeningsInterests = [];
        },
        filteredOpenings(searchedOpeningTypes) {
            return this.openings.filter((opening) =>
                searchedOpeningTypes?.includes(opening?.applicationType?.code)
            );
        },
    },
    mounted() {
        this.loadAppData();
        this.getData();
    },
    mixins: [CommonMixins],
};
</script>
